import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Fingerprint, Hourglass, LogIn, LogOut } from 'lucide-react';
import type { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser';
import {
  evaluateLocation,
  LOCATION_SETTLE_SECONDS,
  UNVERIFIED_ACCURACY_M,
  type PunchType,
  fmtDateLong,
  fmtTime as fmtTimeShared,
} from '@siva/shared';
import { api, ApiError, isUnreachable, post } from '../../lib/api.js';
import { LAST_PUNCH_STATUS, recall, remember } from '../../lib/offline.js';
import { OfflineNote } from '../../lib/OfflineNote.js';
import { appInfo, integrityToken, isNativeApp, nativeLocation } from '../../lib/native.js';
import { syncShiftReminder } from '../../lib/shift-reminder.js';
import { runningMinutes } from '../../lib/punch-clock.js';
import { errorMessage, passkeyAssertion } from '../../lib/passkey.js';
import { Alert, Spinner, shiftLabel } from '../../lib/ui.js';
import { useAuth } from '../../lib/auth.js';
import { HomeWeek } from '../HomeWeek.js';
import { useGeolocation } from '../../lib/useGeolocation.js';
import { onLaunchAndResume, versionState, type VersionState } from '../../lib/app-version.js';
import { UpdateRequired } from './UpdateRequired.js';
import { missingBankLabels, type BankDetails } from './BankDetailsPage.js';
import { SlideOver } from '../../lib/SlideOver.js';
import { t } from '../../i18n/index.js';

type Status = {
  server_time: string;
  work_date: string;
  timezone: string;
  office: {
    configured: boolean;
    lat: number | null;
    lng: number | null;
    radius_m: number;
    name: string;
  };
  open_shift: { since: string; punch_id: string } | null;
  next: PunchType;
  rate_limit_wait_seconds: number;
  punches: {
    id: string;
    type: PunchType;
    server_time: string;
    source: string;
    weak_gps: boolean;
    location_unverified: boolean;
  }[];
  worked_minutes: number;
  worked_minutes_running: number;
  target_minutes: number;
  /** When the hours will be complete if the shift keeps going; null when nothing is waiting. */
  reminder_at: string | null;
};

type PunchResult = {
  ok: boolean;
  punch: Status['punches'][number];
  distance_m: number;
  weak_gps: boolean;
  location_unverified: boolean;
  status: Status;
};

const hm = (min: number) => `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, '0')}m`;

export function PunchPage() {
  const [status, setStatus] = useState<Status | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** Set when the figures on screen are remembered rather than fresh, and when they were true. */
  const [knownAt, setKnownAt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** A refusal may carry a heading when it needs to be unmistakable; most do not. */
  const [notice, setNotice] = useState<{
    kind: 'success' | 'error' | 'info';
    title?: string;
    text: string;
  } | null>(null);
  const [tick, setTick] = useState(0);
  /**
   * When the status on screen was loaded, on the monotonic clock. The running total is that
   * status plus the time since — measured with performance.now(), which a worker cannot change.
   * It was Date.now() minus the server's time, and a worker who set their phone a day ahead
   * watched "1h 23m" become "9h 07m" while the server, correctly, still said 1h 23m.
   */
  const [loadedMono, setLoadedMono] = useState(() => performance.now());
  const [online, setOnline] = useState(navigator.onLine);
  const [showHelp, setShowHelp] = useState(false);
  const [askingReason, setAskingReason] = useState(false);
  /**
   * A device check refused the last punch. Until a fresh position is taken, the button must not
   * sit there green and tappable as though the next tap might work — it would only spend a
   * fingerprint on the same refusal. Nothing here says what was wrong, in the app or on screen.
   */
  const [needsFreshFix, setNeedsFreshFix] = useState(false);
  const [checking, setChecking] = useState(false);
  /**
   * Whether this build may punch at all. Checked on launch and on every resume — a phone in a
   * pocket never launches again — and also set from the server's own refusal, which is the
   * authority: the app's copy of the minimum can be stale, the server's cannot.
   */
  const [version, setVersion] = useState<VersionState | null>(null);
  const [versionChecking, setVersionChecking] = useState(false);
  const geo = useGeolocation(true);
  const { me } = useAuth();

  useEffect(
    () =>
      onLaunchAndResume(() => {
        void versionState().then(setVersion);
      }),
    [],
  );

  /**
   * Ask again, now.
   *
   * `force` skips the cache but does not throw it away, and that distinction is the whole
   * behaviour of this button with no signal: the failed attempt falls back to the last answer we
   * had, so the screen keeps saying "your build is too old" and adds that we could not check.
   * Clearing the cache first made a failed retry return "unknown", the blocking screen vanished
   * and the punch button came back — on a build the server will still refuse, so the worker would
   * have spent a fingerprint to be told the same thing.
   */
  const recheckVersion = async () => {
    setVersionChecking(true);
    try {
      setVersion(await versionState({ force: true }));
    } finally {
      setVersionChecking(false);
    }
  };

  /**
   * What the pay slip still needs from this worker. Their own record, read once per visit to
   * this screen (the bank page is a route away, so coming back re-reads it); the reminder is a
   * line with a link, never anything that stops a punch.
   */
  /** Set when the server refuses a punch because this worker must use the app. */
  const [needsApp, setNeedsApp] = useState(false);
  const [bankMissing, setBankMissing] = useState<string[]>([]);
  useEffect(() => {
    api<BankDetails>('/me/bank')
      .then((b) => setBankMissing(missingBankLabels(b)))
      .catch(() => setBankMissing([]));
  }, []);

  const load = useCallback(async () => {
    try {
      const next = await api<Status>('/punch/status');
      setStatus(next);
      setLoadedMono(performance.now());
      remember(LAST_PUNCH_STATUS, next);
      // A live answer, so the phone's alarm follows it — including a punch-in made from a
      // browser, which this app only learns about here. Never from a remembered status.
      void syncShiftReminder(next);
      setKnownAt(null);
      setLoadError(null);
    } catch (err) {
      setLoadError(errorMessage(err));
      // Opened cold with no signal: show the last thing we knew rather than an empty screen.
      // Only ever as a fallback — a live answer always wins, and no punch is made from this.
      if (isUnreachable(err)) {
        setStatus((current) => {
          if (current) return current;
          const last = recall<Status>(LAST_PUNCH_STATUS);
          if (!last) return current;
          setKnownAt(last.at);
          return last.value;
        });
      }
    }
  }, []);

  useEffect(() => {
    void load();
    const poll = setInterval(() => void load(), 30_000);
    const clock = setInterval(() => setTick((n) => n + 1), 1_000);
    const on = () => {
      setOnline(true);
      void load(); // do not make them wait for the next poll
    };
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      clearInterval(poll);
      clearInterval(clock);
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, [load]);

  // Nothing has ever loaded: there is nothing to keep, so show the reason and a way to retry.
  if (loadError && !status)
    return (
      <section className="card">
        <Alert>{loadError}</Alert>
        <button type="button" onClick={() => void load()}>
          {t('try_again')}
        </button>
      </section>
    );
  if (!status) return <Spinner />;

  const tz = status.timezone;
  const fmtTime = (iso: string) => fmtTimeShared(iso, tz);

  // Running minutes: the server's figure at load time plus the time since, on the monotonic
  // clock. Remembered figures (no signal) are not extrapolated: they say when they were true.
  const sinceLoadMs = knownAt ? 0 : Math.max(0, performance.now() - loadedMono);
  const runningMin = runningMinutes(status, sinceLoadMs);
  const waitLeft = Math.max(0, status.rate_limit_wait_seconds - Math.floor(sinceLoadMs / 1000));
  void tick;

  const office =
    status.office.configured && status.office.lat !== null && status.office.lng !== null
      ? { lat: status.office.lat, lng: status.office.lng, radius_m: status.office.radius_m }
      : null;
  const fix = geo.fix
    ? { lat: geo.fix.lat, lng: geo.fix.lng, accuracy_m: geo.fix.accuracy_m }
    : null;
  const verdict = office ? evaluateLocation(fix, office) : null;

  // The phone gets 30 s from the start of watching to produce a usable fix (≤ 500 m). Until then
  // the button waits quietly: no red message, no attempt sent. After that, whatever the phone has
  // is used: a poor fix is accepted as "unverified" and flagged; no fix at all is reported.
  const sinceStart = geo.startedAt ? Date.now() - geo.startedAt : 0;
  const poorFix = !!fix && fix.accuracy_m > UNVERIFIED_ACCURACY_M;
  const permissionSettled = geo.status === 'denied' || geo.status === 'unsupported';
  const settling =
    !permissionSettled && (!fix || poorFix) && sinceStart < LOCATION_SETTLE_SECONDS * 1000;

  // Why the button is disabled, in priority order. null = ready.
  let blocker: string | null = null;
  let deniedHelp = false;
  if (!online) blocker = t('punch_offline');
  else if (!office) blocker = t('punch_office_not_configured');
  else if (geo.status === 'unsupported') blocker = t('punch_location_unsupported');
  else if (geo.status === 'denied') {
    blocker = t('punch_location_denied_title');
    deniedHelp = true;
  } else if (settling)
    blocker = null; // shown as "Locating…" in the chip, never as a red message
  else if (!fix) blocker = t('punch_location_unavailable');
  else if (verdict && !verdict.ok && verdict.reason === 'outside_geofence')
    blocker = t('punch_outside', {
      distance: Math.round(verdict.distance_m),
      radius: office.radius_m,
    });
  else if (verdict && !verdict.ok) blocker = t('punch_location_unavailable');
  else if (waitLeft > 0) blocker = t('punch_wait', { seconds: waitLeft });

  const weak = !!verdict?.ok && verdict.weak_gps;
  const unverified = !!verdict?.ok && verdict.unverified;
  const outside = !!verdict && !verdict.ok && verdict.reason === 'outside_geofence';
  const disabled = settling || (!!blocker && !outside) || busy || needsFreshFix;

  /** Outside the fence the button still responds: the server records the attempt for the admin. */
  const reportOutside = async () => {
    if (!fix || busy || !outside) return;
    setBusy(true);
    try {
      await post('/punch/attempt', { location: fix });
    } catch {
      /* best effort; the message below is shown regardless */
    } finally {
      setBusy(false);
    }
    setNotice({ kind: 'error', text: blocker ?? '' });
  };

  /** Leaving before the day's hours are done: the app asks why, and then punches out anyway. */
  const leavingEarly =
    status.next === 'out' &&
    status.target_minutes > 0 &&
    status.worked_minutes_running < status.target_minutes;

  const punch = async (early?: { code: string; reason: string }) => {
    if (!fix || busy) return;
    if (leavingEarly && !early) {
      setAskingReason(true);
      return;
    }
    setBusy(true);
    setNotice(null);
    try {
      // Inside the app, the punch uses a reading taken now from the platform rather than the
      // watched position behind the distance chip, and carries what only the platform can say:
      // whether that reading was invented, and whether Android will vouch for this phone. The
      // verdict is bound to this challenge, so it cannot be lifted onto another punch.
      //
      // Every one of these is optional. In a browser they are all absent and nothing changes;
      // in the app, a failure to obtain any of them still lets the punch proceed, because the
      // server is what decides what a punch with no verdict is worth.
      //
      // Order matters to the person holding the phone. The fix and the verdict used to be
      // awaited *before* the fingerprint prompt, so a worker tapped Punch and looked at a
      // spinner for ten or fifteen seconds while the GPS settled, then got asked for a finger
      // — and the sixty-second challenge sometimes ran out under them. Now the position read
      // starts the moment they tap, the prompt appears the moment the challenge is back, and
      // the fix and the verdict finish in the background while the finger is on the sensor.
      const nativeLocationP = isNativeApp() ? nativeLocation() : Promise.resolve(null);
      const { challengeId, options } = await post<{
        challengeId: string;
        options: PublicKeyCredentialRequestOptionsJSON;
      }>('/punch/challenge');
      const tokenP = isNativeApp() ? integrityToken(challengeId) : Promise.resolve(null);
      const infoP = isNativeApp() ? appInfo() : Promise.resolve(null);

      // Android is asked for the passkey before the WebView is, exactly as sign-in does. A
      // WebView that was never granted WebAuthn rejects instantly with the same error as somebody
      // dismissing the prompt, so this screen used to say the prompt was cancelled for a prompt
      // that never appeared.
      const response = await passkeyAssertion(options);

      const [native, token, info] = await Promise.all([nativeLocationP, tokenP, infoP]);
      const punchLocation = native
        ? { lat: native.lat, lng: native.lng, accuracy_m: native.accuracy_m }
        : fix;
      const r = await post<PunchResult>('/punch', {
        challengeId,
        response,
        type: status.next,
        location: punchLocation,
        ...(native ? { mock_location: native.mock } : {}),
        ...(token ? { integrity_token: token } : {}),
        ...(info
          ? { app_version_code: info.version_code, app_version_name: info.version_name }
          : {}),
        ...(early ? { early_out_reason_code: early.code, early_out_reason: early.reason } : {}),
      });
      setAskingReason(false);
      setStatus(r.status);
      setLoadedMono(performance.now());
      void syncShiftReminder(r.status);
      // First successful punch on this device: the install card may show from now on.
      try {
        localStorage.setItem('siva.firstPunchDone', '1');
      } catch {
        /* private mode */
      }
      window.dispatchEvent(new CustomEvent('siva:punched'));
      const time = fmtTime(r.punch.server_time);
      const isIn = r.punch.type === 'in';
      if (r.location_unverified) {
        setNotice({
          kind: 'info',
          text: isIn ? t('punch_done_in_unverified') : t('punch_done_out_unverified'),
        });
      } else {
        setNotice({
          kind: r.weak_gps ? 'info' : 'success',
          text:
            (isIn ? t('punch_done_in', { time }) : t('punch_done_out', { time })) +
            (r.weak_gps ? ` ${t('punch_weak_gps', { accuracy: Math.round(fix.accuracy_m) })}` : ''),
        });
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === 'outside_geofence') {
        setNotice({
          kind: 'error',
          text: t('punch_outside', {
            distance: Number(err.details.distance_m ?? 0),
            radius: Number(err.details.radius_m ?? status.office.radius_m),
          }),
        });
      } else {
        const title =
          err instanceof ApiError && typeof err.details.title === 'string'
            ? err.details.title
            : undefined;
        // Refused for punching outside the app: the message alone leaves them stuck, so the
        // page that explains how to install it is offered with it.
        if (err instanceof ApiError && err.code === 'app_required') setNeedsApp(true);
        // A fake-GPS refusal is the title and nothing else: the owner asked for the warning
        // alone. The longer explanation stays in the server's answer, for the flag record.
        const titleOnly = err instanceof ApiError && err.code === 'mock_location' && title;
        setNotice(
          titleOnly
            ? { kind: 'error', text: titleOnly }
            : { kind: 'error', ...(title ? { title } : {}), text: errorMessage(err) },
        );
      }
      if (
        err instanceof ApiError &&
        (err.code === 'mock_location' || err.code === 'integrity_failed')
      ) {
        setNeedsFreshFix(true);
      }
      // The server refused this build. Its answer beats the app's cached one, which may have been
      // taken before the minimum was raised.
      if (err instanceof ApiError && err.code === 'app_too_old') {
        setVersion(await versionState({ force: true }));
      }
      await load();
    } finally {
      setBusy(false);
    }
  };

  const recheck = async () => {
    setChecking(true);
    try {
      const fresh = isNativeApp() ? await nativeLocation() : null;
      if (!fresh || !fresh.mock) {
        setNeedsFreshFix(false);
        setNotice(null);
      }
    } finally {
      setChecking(false);
    }
  };

  const chip = () => {
    if (settling) {
      return (
        <span className="locating">
          <span className="spin" aria-hidden="true" />
          {fix
            ? t('punch_locating_accuracy', { accuracy: Math.round(fix.accuracy_m) })
            : t('punch_locating')}
        </span>
      );
    }
    if (fix && verdict && office) {
      if (unverified) {
        return t('punch_unverified_chip', { accuracy: Math.round(fix.accuracy_m) });
      }
      return t('punch_distance', {
        distance: Math.round(
          verdict.ok ? verdict.distance_m : ((verdict as { distance_m?: number }).distance_m ?? 0),
        ),
        accuracy: Math.round(fix.accuracy_m),
      });
    }
    return geo.status === 'locating' || geo.status === 'prompt' ? t('punch_locating') : '';
  };

  // The three figures under the button: when the day began, when it last stopped, and what is
  // left of the target. All from today's punches and the running total already on screen.
  const firstIn = status.punches.find((p) => p.type === 'in') ?? null;
  const lastOut = [...status.punches].reverse().find((p) => p.type === 'out') ?? null;
  const leftMin = Math.max(0, status.target_minutes - runningMin);
  const shift = me?.user?.shift;

  return (
    <div className="stack">
      {/* Nothing fresh, and nothing wrong: these are remembered figures and we say when from.
          The punch button disables itself on its own rules, so this never has to warn anyone. */}
      {knownAt && <OfflineNote at={knownAt} onRetry={() => void load()} />}

      <section className={`card punch-card ${knownAt ? 'stale' : ''}`}>
        {notice && (
          <Alert kind={notice.kind}>
            {notice.title ? (
              <>
                <strong className="alert-title">{notice.title}</strong>
                {notice.text}
              </>
            ) : (
              notice.text
            )}
          </Alert>
        )}
        {/* A refresh failed but the screen still stands from a live load: a quieter note. */}
        {loadError && !notice && !knownAt && <Alert kind="warn">{t('punch_stale')}</Alert>}
        {needsApp && (
          <p className="alert warn bank-reminder">
            {t('punch_needs_app')} <Link to="/get-the-app">{t('punch_needs_app_link')}</Link>
          </p>
        )}
        {bankMissing.length > 0 && !notice && (
          <p className="alert warn bank-reminder">
            {t('punch_bank_reminder', { fields: bankMissing.join(', ') })}{' '}
            <Link to="/profile/bank">{t('punch_bank_add')}</Link>
          </p>
        )}

        {chip() && <p className="distance">{chip()}</p>}

        {/* The day so far, as the card's clock. The same element and the same words as before —
            "6h 12m / 9h 00m" — moved up from the card below it. */}
        <div className="punch-clock">
          <p className="big-numeral">
            {hm(runningMin)}
            <small>/ {hm(status.target_minutes)}</small>
          </p>
          <p className="punch-date">
            {fmtDateLong(status.work_date)}
            {shift ? ` · ${shiftLabel(shift)}` : ''}
          </p>
        </div>
        {/* Too old to punch: the button is replaced rather than the app, so everything else on
            the tab bar still works for somebody who cannot update this minute. */}
        {version?.standing === 'too_old' ? (
          <UpdateRequired
            state={version}
            onRecheck={() => void recheckVersion()}
            checking={versionChecking}
          />
        ) : (
          <button
            type="button"
            className={`punch-button ${status.next}${blocker || needsFreshFix ? ' looks-disabled' : ''}`}
            onClick={outside ? reportOutside : () => void punch()}
            disabled={disabled}
            aria-describedby="punch-blocker"
          >
            {/* Decoration only: the button's name is still exactly its words. */}
            <Fingerprint className="punch-icon" size={40} strokeWidth={1.8} aria-hidden="true" />
            <span>
              {busy ? t('punch_working') : status.next === 'in' ? t('punch_in') : t('punch_out')}
            </span>
          </button>
        )}

        {blocker && (
          <p id="punch-blocker" className="blocker" role="status">
            {blocker}
          </p>
        )}
        {needsFreshFix && (
          <p className="row center">
            <button type="button" className="secondary" onClick={() => void recheck()} disabled={checking}>
              {checking ? t('punch_checking') : t('punch_check_again')}
            </button>
          </p>
        )}
        <div className="punch-trio">
          <div>
            <LogIn className="in" size={18} aria-hidden="true" />
            <strong>{firstIn ? fmtTime(firstIn.server_time) : '--:--'}</strong>
            <span>{t('punch_trio_in')}</span>
          </div>
          <div>
            <LogOut className="out" size={18} aria-hidden="true" />
            <strong>{lastOut ? fmtTime(lastOut.server_time) : '--:--'}</strong>
            <span>{t('punch_trio_out')}</span>
          </div>
          <div>
            <Hourglass className="left" size={18} aria-hidden="true" />
            <strong>{leftMin > 0 ? hm(leftMin) : t('punch_trio_done')}</strong>
            <span>{t('punch_trio_left')}</span>
          </div>
        </div>

        {unverified && !blocker && !settling && (
          <p className="muted">{t('punch_unverified_note')}</p>
        )}
        {weak && !blocker && (
          <p className="muted">{t('punch_weak_gps', { accuracy: Math.round(fix!.accuracy_m) })}</p>
        )}

        {deniedHelp && (
          <div className="help">
            <p>{t('punch_location_denied_intro')}</p>
            <ul>
              <li>{t('punch_location_denied_chrome')}</li>
              <li>{t('punch_location_denied_safari')}</li>
            </ul>
          </div>
        )}

        {status.open_shift && (
          <p className="muted">{t('punch_since', { time: fmtTime(status.open_shift.since) })}</p>
        )}

        {office && geo.status !== 'unsupported' && !deniedHelp && (
          <p className="help-link-row">
            <button
              type="button"
              className="link"
              onClick={() => setShowHelp((v) => !v)}
              aria-expanded={showHelp}
            >
              {t('punch_location_help_link')}
            </button>
          </p>
        )}
        {showHelp && (
          <div className="help">
            <ul>
              <li>{t('punch_location_help_ios')}</li>
              <li>{t('punch_location_help_android')}</li>
            </ul>
          </div>
        )}
      </section>

      <section className="card">
        <h3>{t('punch_today')}</h3>
        {status.punches.length === 0 ? (
          <p className="muted">{t('punch_no_punches')}</p>
        ) : (
          <ul className="list punches">
            {status.punches.map((p) => (
              <li key={p.id} className="row between">
                <span>
                  <span className={`dot ${p.type}`} aria-hidden="true" />
                  <strong>{p.type === 'in' ? t('punch_type_in') : t('punch_type_out')}</strong>{' '}
                  {fmtTime(p.server_time)}
                </span>
                <span className="muted">
                  {p.source === 'auto_close'
                    ? t('punch_auto_closed')
                    : p.source === 'admin'
                      ? t('punch_by_admin')
                      : ''}
                  {p.weak_gps ? ` · ${t('punch_weak_gps_short')}` : ''}
                  {p.location_unverified ? ` · ${t('punch_unverified_short')}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* This week and the last few days, from the Hours and Weekly Off tabs' own data. */}
      <HomeWeek />

      {askingReason && (
        <EarlyOutReason
          worked={status.worked_minutes_running}
          target={status.target_minutes}
          busy={busy}
          onCancel={() => setAskingReason(false)}
          onSubmit={(code, reason) => void punch({ code, reason })}
        />
      )}
    </div>
  );
}

const REASONS = ['personal_emergency', 'unwell', 'work_completed', 'other'] as const;

/**
 * Asked for before the fingerprint, never after: the punch is not in question, only the reason.
 * An admin reviews it later; nothing here stops anyone leaving.
 */
function EarlyOutReason({
  worked,
  target,
  busy,
  onCancel,
  onSubmit,
}: {
  worked: number;
  target: number;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (code: string, reason: string) => void;
}) {
  const [code, setCode] = useState<string>('');
  const [text, setText] = useState('');
  const tooShort = text.trim().length < 10;

  return (
    <SlideOver open title={t('early_out_title')} onClose={onCancel}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (!code || tooShort) return;
          onSubmit(code, text.trim());
        }}
      >
        <p>{t('early_out_body', { worked: hm(worked), target: hm(target) })}</p>
        {/* The rows are laid out by the div, not by the fieldset.
            Chromium has never handled display: grid on a <fieldset> the way it does on anything
            else, and the version in the Android WebView renders it as plain block flow — which
            turned inline-flex labels into an inward-stepping staircase with the radios floating
            in the middle of each row. It looked correct in desktop Chrome throughout. A plain
            div does the same job everywhere. */}
        <fieldset className="option-list">
          <legend>{t('early_out_pick')}</legend>
          <div className="option-list-items">
            {REASONS.map((r) => (
              <label key={r} className={`option ${code === r ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="early-out-reason"
                  value={r}
                  checked={code === r}
                  onChange={() => setCode(r)}
                />
                <span>{t(`early_out_${r}` as 'early_out_unwell')}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <label>
          {t('early_out_detail')}
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            minLength={10}
            maxLength={200}
            required
            placeholder={t('early_out_detail_hint')}
          />
          <span className="muted block-help">{t('early_out_detail_help')}</span>
        </label>
        <div className="row">
          <button type="submit" disabled={busy || !code || tooShort}>
            {t('early_out_confirm')}
          </button>
          <button type="button" className="ghost" onClick={onCancel} disabled={busy}>
            {t('cancel')}
          </button>
        </div>
      </form>
    </SlideOver>
  );
}
